import { ColDef, IGetRowsParams } from 'ag-grid-community';
import {
  BehaviorSubject,
  Observable,
  observableCombineLatest,
  rx,
  Subject,
} from '@shared/rx';
import {
  chunk,
  compact,
  differenceBy,
  filter,
  find,
  flatten,
  get,
  isArray,
  isEqual,
  isObject,
  isString,
  isPlainObject,
  map,
  merge,
  mergeWith,
  pick,
  pickBy,
  isEmpty,
  toUpper,
  includes,
} from 'lodash';
import {
  DEFAULT_PAGE_SIZE,
  ENTITY_ID_PATH,
  MAX_ITEMS_PER_QUERY,
} from '@config/global-constants';
import { XVIError } from '@shared/models';
import {
  IAdditionalDataSourceItems,
  IApiListResponse,
  IBaseEntityType,
  IDataSourceOptions,
  FilterModel,
  IFilterQuery,
  SelectedItemsMapper,
  SortModelField,
  SyncOrAsync,
  BackendApiType,
} from '@shared/types';
import { isObservable, isPromise, queryWithEntityIds } from '@shared/utils';
import { BaseService } from '../base';
import {
  composeFilterGroups,
  mergeCustomizer,
  parseFilterGroups,
} from './datasource-utils';
import {
  IColumnFilterWithType,
  IComposeFilterModel,
  IQuickFilterFieldSettings,
} from './';
import { Primitive } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';

const SERVICE_PICKS: string[] = [
  'entityType',
  'headerNames',
  'defaultQuery',
  'listProjection',
  'captionProjection',
  'pageSize',
];

export class DataSource<TEntityService extends BaseService = BaseService>
  implements IComposeFilterModel
{
  public entityType: string;
  public headerNames: ColDef[];
  public defaultQuery: IFilterQuery = {};
  public listProjection: string[];
  public captionProjection: string[];
  public busy: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public error: Subject<XVIError> = new Subject();
  public exhausted: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public items: BehaviorSubject<any[]> = new BehaviorSubject([]);
  public empty: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public selectedItems: BehaviorSubject<any[]> = new BehaviorSubject([]);
  public availableItems: BehaviorSubject<any[]> = new BehaviorSubject([]);
  public useSelectedStores: boolean = false;
  public sortModel: SortModelField[] = [];
  public filterModel: FilterModel = {};
  public pageSize: number = DEFAULT_PAGE_SIZE;
  public requestPromise: Promise<any> = Promise.resolve();
  // Additional property to be used for selected items display.
  // Might be customly remapped to satisfy user needs.
  public visibleSelectedItemsObs: Observable<any[]>;
  public maxItemsPerQuery: number = MAX_ITEMS_PER_QUERY;
  public total: number = -1;
  public rowCount: number = -1;
  public quickFilterFields: string[];
  public preloadedItemsArray: any[] = [];
  public selectedItemsMapper: SelectedItemsMapper;
  public additionalItems: IAdditionalDataSourceItems[] = [];
  public optionsList: IBaseEntityType[] = [];

  public constructor(
    public service: TEntityService,
    options: IDataSourceOptions,
  ) {
    mergeWith(this, pick(service, SERVICE_PICKS), options, mergeCustomizer);
    observableCombineLatest([
      this.items.asObservable(),
      this.selectedItems.asObservable(),
    ])
      .pipe(
        rx.map(([items, selected]) =>
          differenceBy(items, selected, this.getItemValue),
        ),
      )
      .subscribe((items) => {
        // XDM-25477: update total row count after user changes took place
        if (this.total > -1 && this.selectedItems.value.length) {
          this.rowCount = this.total - this.selectedItems.value.length;
        }
        this.availableItems.next(items);
      });
    this.visibleSelectedItemsObs = this.selectedItems.asObservable();
    this.selectedItemsMapper = options.selectedItemsMapper;
    this.initPreloadedItems(options.preloadedItems);
    this.additionalItems = options.additionalItems || this.additionalItems;
    this.setAdditionalItems();
  }

  public setFilter(filterText): boolean {
    const newFilterModel: FilterModel = this.composeFilterModel(filterText);
    const filterChanged: boolean = !isEqual(newFilterModel, this.filterModel);
    if (filterChanged) {
      this.filterModel = newFilterModel;
    }

    return filterChanged;
  }

  public setSelection(userSelection: any[]): void {
    // find items by value in selectedItems and items
    const newSelection: any[] = compact(
      map(
        userSelection,
        (id) =>
          find(
            this.selectedItems.value,
            (item) => this.getItemValue(item) === id,
          ) ||
          find(
            this.preloadedItemsArray,
            (item) => this.getItemValue(item) === id,
          ) ||
          find(this.items.value, (item) => this.getItemValue(item) === id) ||
          id,
      ),
    );
    // emit selectedItems filled with items, that we already have
    this.selectedItems.next(filter(newSelection, isObject));

    // request items by id
    const docsToFetch: string[] = filter(newSelection, isString);
    if (!docsToFetch.length) {
      return;
    }
    this.requestPromise = this.requestPromise
      .then(() => this.busy.next(true))
      .then(() => this.fetchSelection(docsToFetch))
      // map selection by fetched items
      .then((items) =>
        map(
          newSelection,
          (id) => find(items, (item) => this.getItemValue(item) === id) || id,
        ),
      )
      .then((items) => filter(items, isObject))
      // emit new selectedItems
      .then((combinedSelection) => this.selectedItems.next(combinedSelection))
      .then(() => this.busy.next(false));
  }

  public fetchSelection(entitiesToFetch: string[]): Promise<IBaseEntityType[]> {
    const chunks = chunk(entitiesToFetch, this.maxItemsPerQuery);
    const entityIdPath = get(
      this.service,
      ['config', 'entityIdPath'],
      ENTITY_ID_PATH,
    );

    return Promise.all(
      chunks.map((item) =>
        this.fetchList(queryWithEntityIds(item, entityIdPath)),
      ),
    ).then((arrayOfResults) => flatten(compact(map(arrayOfResults, 'items'))));
  }

  public nextPage(): Promise<IBaseEntityType[]> {
    this.requestPromise = this.requestPromise.then(() => {
      if (this.exhausted.value) {
        return [];
      }
      const select: string = get(this, 'listProjection', []).join();
      const query: IFilterQuery = pickBy(
        {
          select,
          orderby: map(
            this.sortModel,
            ({ colId, sort }) => `${colId} ${sort}`,
          ).join(),
          filter: this.composeFilterQueryParameter(this.filterModel),
          top: this.pageSize,
          skip: this.getSkipLength(),
        },
        Boolean,
      );

      this.busy.next(true);

      return this.fetchList(query)
        .then(({ items, total }) => {
          this.total = total;
          this.rowCount = total - this.selectedItems.value.length;
          this.items.next([].concat(this.items.value, items));
          if (total <= this.items.value.length) {
            this.exhausted.next(true);
            this.empty.next(!total);
          }

          return items;
        })
        .catch((error) => {
          this.items.next([]);
          // eslint-disable-next-line  no-console
          console.log(error);

          return [];
        })
        .then((items) => {
          this.optionsList = items;
          this.busy.next(false);

          return items;
        });
    });

    return this.requestPromise;
  }

  public getFilteringResults(text: string): Promise<IBaseEntityType[]> {
    this.busy.next(true);
    const result = isEmpty(text)
      ? [...this.optionsList]
      : filter(this.optionsList, (item) =>
          includes(toUpper(item.name), toUpper(text)),
        );
    this.exhausted.next(true);
    this.empty.next(!result.length);
    this.items.next(result);
    this.busy.next(false);

    return Promise.resolve(result);
  }

  public purge(): void {
    this.exhausted.next(false);
    this.empty.next(false);
    this.items.next([]);
  }

  public composeFilterQueryParameter(filterModel): string {
    const isCombinedFilterQueryWithAnyOf = get(
      this,
      'isCombinedFilterQueryWithAnyOf',
      false,
    );
    const filterGroups = composeFilterGroups(
      filterModel,
      isCombinedFilterQueryWithAnyOf,
    );
    const filterProps: string = parseFilterGroups(filterGroups);

    const defaultFilter: string = get(this, 'defaultQuery.filter', '');

    if (!defaultFilter) {
      return filterProps;
    }

    let filterParameter: string = `${defaultFilter}`;

    if (filterProps) {
      filterParameter = `(${filterParameter}) and (${filterProps})`;
    }

    return filterParameter;
  }

  public getRows(params: IGetRowsParams): void {
    const { startRow, endRow, sortModel, filterModel } = params;
    const select: string = get(this, 'listProjection', []).join(',');
    const includeEntities: any[] = get(
      this.service,
      'config.list.documentQuery.include_entities',
      [],
    );

    const query: IFilterQuery = pickBy(
      {
        select,
        orderby: map(sortModel, ({ colId, sort }) => `${colId} ${sort}`).join(
          ',',
        ),
        filter: this.composeFilterQueryParameter(filterModel),
        top: endRow - startRow,
        skip: startRow,
      },
      Boolean,
    );

    if (includeEntities.length) {
      query.include_entities = includeEntities;
    }

    this.busy.next(true);
    this.fetchList(query)
      .then(({ items, total }) => params.successCallback(items, total))
      .catch(() => params.failCallback())
      .then(() => this.busy.next(false));
  }

  public fetchList(query): Promise<IApiListResponse> {
    const select: string = get(this, 'captionProjection', []).join(',');
    const queryWithDefaults: IFilterQuery = merge(
      { select },
      this.defaultQuery,
      query,
    );

    return this.service
      .fetchList(queryWithDefaults)
      .pipe(rx.first())
      .toPromise()
      .then(
        (result) => result,
        (error) => (this.error.next(error), Promise.reject(error)),
      );
  }

  public replaceSpecialCharactersForOData(text): Primitive | Primitive[] {
    let result: string = text;
    const backendApiType = get(
      this.service,
      'config.backendApiType',
      BackendApiType.XVI,
    );

    switch (backendApiType) {
      case BackendApiType.DM:
        // the parser in DMAPI cannot handle certain characters without escapement
        // more special characters will need to be included, and a thorough unit test
        result = text.replace(/\(/g, '\\(');
        result = result.replace(/\)/g, '\\)');

        return result;
      case BackendApiType.PORTAL:
        // When requesting PORTALAPI and using ODATA filtering, special characters need to be escaped.
        return encodeURIComponent(text);
      default:
        return result;
    }
  }

  public composeFilterModel(text: string): FilterModel {
    if (!text) return {};
    const filterFields = this.getQuickFilterFields();

    // Filter works only for the fields included in headerNames (ag-grid restriction)
    return filterFields.reduce(
      (accumulator, prop) =>
        merge(accumulator, this.getFilterModelByText(text, prop)),
      {},
    );
  }

  public getQuickFilterFields(): (string | IQuickFilterFieldSettings)[] {
    return this.quickFilterFields || this.service.quickFilterFields;
  }

  public refresh(): void {
    this.filterModel = {};
    this.purge();
  }

  /**
   * Searches for a document inside items and selectedItems data by provided entity_id,
   * returns itemText for found item
   */
  public getItemTextByEntityId = (id: string): string => {
    const item =
      find(this.items.value, { entity_id: id } as any) ||
      find(this.selectedItems.value, { entity_id: id } as any);

    return this.getItemText(item) || '';
  };

  public getItemText: TEntityService['getItemText'] = (item: any): string => {
    return this.service.getItemText(item);
  };

  public getItemValue: TEntityService['getItemValue'] = (item: any): string => {
    return this.service.getItemValue(item);
  };

  public initializeSelected(queryFilter, cb): Promise<void> {
    const query: IFilterQuery = pickBy(
      {
        filter: queryFilter,
        top: this.pageSize,
      },
      Boolean,
    );
    this.busy.next(true);

    return this.fetchList(query)
      .then(({ items }) => {
        this.busy.next(false);
        cb(items);
      })
      .catch((error) => {
        this.busy.next(false);
        // eslint-disable-next-line  no-console
        console.log(error);
        cb([]);
      });
  }

  public reloadDataSourceItems(queryFilter: string): void {
    this.defaultQuery = {
      ...this.defaultQuery,
      filter: queryFilter,
    };
    this.setSelection([]);
    this.initializeSelected(queryFilter, (items) => {
      this.items.next(items);
    });
  }

  private async initPreloadedItems(
    preloadedItems: SyncOrAsync<any[]>,
  ): Promise<void> {
    if (isArray(preloadedItems)) {
      this.setPreloadedItems(preloadedItems);
    }
    if (isObservable(preloadedItems)) {
      preloadedItems
        .pipe(rx.first())
        .subscribe((items) => this.setPreloadedItems(items));
    }
    if (isPromise(preloadedItems)) {
      this.setPreloadedItems(await preloadedItems);
    }
  }

  private setPreloadedItems(items: any[]): void {
    this.preloadedItemsArray = items;
  }

  private getFilterModelByText(
    text: string,
    field: string | IQuickFilterFieldSettings,
  ): { [column: string]: IColumnFilterWithType } {
    if (isString(field)) {
      return {
        [<string>field]: {
          filter: this.replaceSpecialCharactersForOData(text),
          filterType: 'text',
          type: 'contains',
        },
      };
    }

    if (isPlainObject(field)) {
      return {
        [(<IQuickFilterFieldSettings>field).filedName]: {
          filter: this.replaceSpecialCharactersForOData(text),
          filterType: (<IQuickFilterFieldSettings>field).filterType || 'text',
          type: (<IQuickFilterFieldSettings>field).type || 'contains',
        },
      };
    }
  }

  private getSkipLength(): number {
    if (!isEmpty(this.additionalItems) && isEmpty(this.filterModel)) {
      this.setAdditionalItems();

      return this.items.value.length - this.additionalItems.length;
    }

    return this.items.value.length;
  }

  private setAdditionalItems(): void {
    if (isEmpty(this.additionalItems) || !isEmpty(this.items.value)) {
      return;
    }
    const itemsValue = isEmpty(this.items.value)
      ? this.additionalItems
      : [...this.additionalItems, ...this.items.value];
    this.items.next(itemsValue);
    this.empty.next(false);
  }
}
